import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Network, ChevronRight, ChevronDown, Users, Layers, Sparkles, Search, Building2 } from "lucide-react";

function spanTone(span) {
  if (span >= 8) return "danger";
  if (span >= 5) return "warning";
  if (span > 0) return "primary";
  return "";
}

function spanChipClass(span) {
  const t = spanTone(span);
  if (t === "danger") return "chip chip-danger";
  if (t === "warning") return "chip chip-warning";
  if (t === "primary") return "chip chip-primary";
  return "chip";
}

function nodeHasDept(n, d) {
  if (n.department === d) return true;
  const kids = n.children || [];
  for (let i = 0; i < kids.length; i++) {
    if (nodeHasDept(kids[i], d)) return true;
  }
  return false;
}

function nodeMeetsSpan(n, m) {
  if (n.span >= m) return true;
  const kids = n.children || [];
  for (let i = 0; i < kids.length; i++) {
    if (nodeMeetsSpan(kids[i], m)) return true;
  }
  return false;
}

export default function OrgChart() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [expanded, setExpanded] = useState(new Set());
  const [minSpan, setMinSpan] = useState(0);
  const [deptFilter, setDeptFilter] = useState("all");
  const [search, setSearch] = useState("");

  useEffect(() => {
    api.get("/company/org")
      .then((res) => setData(res.data))
      .catch(() => toast.error("Failed to load org"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (data && data.roots && data.roots.length && !selected) {
      setSelected(data.roots[0]);
      const s = new Set();
      for (const r of data.roots) {
        s.add(r.id);
        const kids = r.children || [];
        for (const c of kids) s.add(c.id);
      }
      setExpanded(s);
    }
  }, [data, selected]);

  const departments = useMemo(() => {
    if (!data || !data.roots) return [];
    const set = new Set();
    const stack = [...data.roots];
    while (stack.length) {
      const n = stack.pop();
      if (n.department) set.add(n.department);
      const kids = n.children || [];
      for (const c of kids) stack.push(c);
    }
    return Array.from(set).sort();
  }, [data]);

  const toggle = (id) => {
    setExpanded((prev) => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id);
      else s.add(id);
      return s;
    });
  };

  // Flatten the tree into a list of {node, depth, visible} — iterative, no recursion
  const flatRows = useMemo(() => {
    if (!data || !data.roots) return [];
    const rows = [];
    const stack = [];
    for (let i = data.roots.length - 1; i >= 0; i--) stack.push({ node: data.roots[i], depth: 0 });
    while (stack.length) {
      const { node, depth } = stack.pop();
      const kids = node.children || [];
      const matchDept = deptFilter === "all" || nodeHasDept(node, deptFilter);
      const matchSpan = nodeMeetsSpan(node, minSpan);
      if (!matchDept || !matchSpan) continue;
      rows.push({ node, depth, hasChildren: kids.length > 0, isOpen: expanded.has(node.id) });
      if (expanded.has(node.id)) {
        for (let i = kids.length - 1; i >= 0; i--) stack.push({ node: kids[i], depth: depth + 1 });
      }
    }
    return rows;
  }, [data, expanded, minSpan, deptFilter]);

  const flatSearch = useMemo(() => {
    if (!search.trim() || !data || !data.roots) return [];
    const q = search.toLowerCase();
    const results = [];
    const stack = [...data.roots];
    while (stack.length && results.length < 20) {
      const n = stack.pop();
      const nameHit = n.name.toLowerCase().indexOf(q) >= 0;
      const idHit = n.id.toLowerCase().indexOf(q) >= 0;
      if (nameHit || idHit) results.push(n);
      const kids = n.children || [];
      for (const c of kids) stack.push(c);
    }
    return results;
  }, [search, data]);

  const pickById = (id) => {
    if (!data || !data.roots) return;
    const stack = [...data.roots];
    while (stack.length) {
      const n = stack.pop();
      if (n.id === id) { setSelected(n); return; }
      const kids = n.children || [];
      for (const c of kids) stack.push(c);
    }
  };

  if (loading) return <div className="p-10 text-slate-400">Loading org…</div>;

  if (!data || !data.has_hierarchy) {
    return (
      <div className="max-w-3xl mx-auto p-6 lg:p-10">
        <div className="chip mb-3"><Network size={12} /> Org Analytics</div>
        <h1 className="font-display text-4xl font-medium tracking-tight text-slate-900">Org Chart</h1>
        <div className="card mt-8 text-center py-16">
          <Network size={32} className="mx-auto text-slate-300 mb-4" />
          <div className="text-lg font-medium text-slate-900 mb-2">No organizational hierarchy detected</div>
          <div className="text-slate-500 text-sm mb-6">
            Upload an Employee Master file with <span className="mono">employee_id</span> and <span className="mono">manager_id</span> columns.
          </div>
          <Link to={`/t/${slug}/upload`} className="btn-primary">Upload data</Link>
        </div>
      </div>
    );
  }

  const m = data.metrics || {};
  const layerTone = m.layers > 6 ? "warning" : "primary";

  return (
    <div className="max-w-7xl mx-auto p-6 lg:p-10">
      <div className="mb-6 flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="chip chip-primary mb-2"><Network size={12} /> Org Analytics · {data.dataset ? data.dataset.name : ""}</div>
          <h1 className="font-display text-4xl md:text-5xl font-medium tracking-tight text-slate-900">Organization</h1>
          <p className="text-slate-500 mt-3 max-w-lg">Explore the hierarchy, spans of control, and management layers. Click any manager to drill down.</p>
        </div>
        <Link to={`/t/${slug}/ask`} className="btn-ghost">
          <Sparkles size={14} /> Ask about the org
        </Link>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
        <Kpi label="Headcount" value={m.total_employees} icon={<Users size={14} />} />
        <Kpi label="Layers" value={m.layers} icon={<Layers size={14} />} tone={layerTone} />
        <Kpi label="Managers" value={m.managers} sub={`${m.manager_ratio_pct || 0}%`} />
        <Kpi label="ICs" value={m.individual_contributors} />
        <Kpi label="Avg Span" value={m.avg_span} />
        <Kpi label="Max Span" value={m.max_span} tone="primary" />
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-6">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            data-testid="org-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search employee by name or ID…"
            className="input-base pl-9"
          />
          {flatSearch.length > 0 && (
            <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-[var(--border)] rounded-lg shadow-lg z-10 max-h-64 overflow-y-auto scroll-thin">
              {flatSearch.map((r, i) => (
                <button
                  key={r.id + "-" + i}
                  onClick={() => { setSelected(r); setSearch(""); }}
                  className="w-full text-left px-3 py-2 hover:bg-slate-50 border-b border-slate-100"
                >
                  <div className="text-sm text-slate-900">{r.name}</div>
                  <div className="text-xs text-slate-500 mono">{r.id} · {r.department} · L{r.layer}</div>
                </button>
              ))}
            </div>
          )}
        </div>
        <select
          data-testid="dept-filter"
          value={deptFilter}
          onChange={(e) => setDeptFilter(e.target.value)}
          className="input-base max-w-[200px]"
        >
          <option value="all">All departments</option>
          {departments.map((d) => <option key={d} value={d}>{d}</option>)}
        </select>
        <label className="flex items-center gap-2 text-xs text-slate-500">
          Min span
          <input
            type="number"
            min={0}
            max={20}
            value={minSpan}
            onChange={(e) => setMinSpan(Number(e.target.value))}
            className="input-base w-16 text-center"
            data-testid="min-span"
          />
        </label>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 card-flush overflow-hidden">
          <div className="px-5 py-3 border-b border-[var(--border)] flex items-center justify-between bg-slate-50/60">
            <div className="label-caps">Hierarchy</div>
            <div className="text-[10px] text-slate-500 mono">{data.roots.length} roots · Click ▶ to expand</div>
          </div>
          <div className="max-h-[720px] overflow-y-auto scroll-thin p-3">
            {flatRows.map((row) => (
              <TreeRow
                key={row.node.id}
                node={row.node}
                depth={row.depth}
                hasChildren={row.hasChildren}
                isOpen={row.isOpen}
                isSel={selected && selected.id === row.node.id}
                onSelect={setSelected}
                onToggle={toggle}
              />
            ))}
          </div>
        </div>

        <div className="space-y-4">
          <DetailPanel node={selected} setSelected={setSelected} />
          <LayerDist dist={m.layer_distribution} />
          <TopSpans list={m.top_spans} onPick={pickById} />
        </div>
      </div>
    </div>
  );
}

function TreeRow(props) {
  const { node, depth, hasChildren, isOpen, isSel, onSelect, onToggle } = props;
  const rowClass = isSel
    ? "flex items-center gap-2 py-1.5 rounded-lg cursor-pointer bg-[var(--primary-soft)]"
    : "flex items-center gap-2 py-1.5 rounded-lg cursor-pointer hover:bg-slate-50";
  const chevronClass = hasChildren
    ? "text-slate-400 hover:text-slate-900"
    : "text-slate-400 opacity-0";
  const locSuffix = node.location ? " · " + node.location : "";
  return (
    <div
      onClick={() => onSelect(node)}
      data-testid={"org-node-" + node.id}
      className={rowClass}
      style={{ paddingLeft: (depth * 18 + 8) + "px", paddingRight: 8 }}
    >
      <button
        onClick={(e) => { e.stopPropagation(); if (hasChildren) onToggle(node.id); }}
        className={chevronClass}
        data-testid={"toggle-" + node.id}
      >
        {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
      </button>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="text-sm truncate text-slate-800">{node.name}</span>
          <span className="text-[10px] mono text-slate-500">{node.id}</span>
          {node.band ? <span className="chip text-[9px]">{node.band}</span> : null}
        </div>
        <div className="text-[11px] text-slate-500 mono truncate">
          {node.department}{locSuffix}
        </div>
      </div>
      {node.span > 0 ? <span className={spanChipClass(node.span)}>{node.span}</span> : null}
    </div>
  );
}

function DetailPanel({ node, setSelected }) {
  if (!node) return <div className="card text-sm text-slate-500">Select an employee to see details.</div>;
  const st = spanTone(node.span);
  const kids = node.children || [];
  return (
    <div className="card" data-testid="org-detail">
      <div className="mb-3">
        <div className="label-caps mb-1">Employee</div>
        <div className="font-display text-xl font-medium text-slate-900" data-testid="detail-name">{node.name}</div>
        <div className="text-xs text-slate-500 mono">{node.id}</div>
      </div>
      <div className="grid grid-cols-2 gap-3 mb-4">
        <Meta label="Department" value={node.department || "—"} />
        <Meta label="Band" value={node.band || "—"} />
        <Meta label="Location" value={node.location || "—"} />
        <Meta label="Layer" value={"L" + node.layer} />
        <Meta label="Direct reports" value={node.span} tone={st} />
        <Meta label="Total reports" value={node.total_reports} />
      </div>
      {kids.length > 0 ? (
        <div>
          <div className="label-caps mb-2">Direct reports ({kids.length})</div>
          <div className="space-y-1 max-h-56 overflow-y-auto scroll-thin">
            {kids.slice(0, 20).map((c) => (
              <button
                key={c.id}
                onClick={() => setSelected(c)}
                data-testid={"report-" + c.id}
                className="w-full text-left px-2 py-1.5 rounded hover:bg-slate-50 text-sm"
              >
                <div className="flex items-center justify-between">
                  <span className="truncate text-slate-800">{c.name}</span>
                  {c.span > 0 ? <span className={spanChipClass(c.span)}>{c.span}</span> : null}
                </div>
                <div className="text-[11px] text-slate-500 mono truncate">{c.department} · {c.id}</div>
              </button>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function Meta({ label, value, tone }) {
  let valCls = "text-slate-900";
  if (tone === "danger") valCls = "text-red-600";
  else if (tone === "warning") valCls = "text-amber-600";
  else if (tone === "primary") valCls = "text-[var(--primary)]";
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className={"text-sm font-medium mt-0.5 " + valCls}>{value}</div>
    </div>
  );
}

function LayerDist({ dist }) {
  if (!dist || !dist.length) return null;
  let max = 0;
  for (const d of dist) if (d.count > max) max = d.count;
  return (
    <div className="card">
      <div className="flex items-center gap-2 mb-3">
        <Layers size={13} className="text-[var(--primary)]" />
        <div className="label-caps">Employees per layer</div>
      </div>
      <div className="space-y-2">
        {dist.map((d) => {
          const w = max ? (d.count / max) * 100 : 0;
          return (
            <div key={d.layer} className="flex items-center gap-3">
              <div className="text-xs mono text-slate-500 w-6">L{d.layer}</div>
              <div className="flex-1 h-6 bg-slate-100 rounded overflow-hidden">
                <div className="h-full bg-[var(--primary)] rounded" style={{ width: w + "%" }} />
              </div>
              <div className="text-xs mono text-slate-700 w-10 text-right">{d.count}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function TopSpans({ list, onPick }) {
  if (!list || !list.length) return null;
  return (
    <div className="card">
      <div className="flex items-center gap-2 mb-3">
        <Building2 size={13} className="text-amber-600" />
        <div className="label-caps">Largest spans of control</div>
      </div>
      <div className="space-y-2">
        {list.map((s, i) => (
          <button
            key={s.id + "-" + i}
            onClick={() => onPick(s.id)}
            data-testid={"top-span-" + s.id}
            className="w-full flex items-center justify-between text-left px-2 py-1.5 rounded hover:bg-slate-50"
          >
            <div className="min-w-0">
              <div className="text-sm text-slate-900 truncate">{s.name}</div>
              <div className="text-[11px] text-slate-500 mono truncate">{s.department} · {s.total_reports} total</div>
            </div>
            <span className={spanChipClass(s.span)}>{s.span}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function Kpi({ label, value, sub, icon, tone }) {
  let valCls = "text-slate-900";
  if (tone === "primary") valCls = "text-[var(--primary)]";
  else if (tone === "warning") valCls = "text-amber-600";
  else if (tone === "danger") valCls = "text-red-600";
  const display = value === undefined || value === null ? "—" : String(value);
  return (
    <div className="card">
      <div className="flex items-center gap-2 mb-2">
        {icon ? <span className="text-slate-400">{icon}</span> : null}
        <span className="label-caps truncate">{label}</span>
      </div>
      <div className={"font-display text-2xl font-medium " + valCls}>
        {display}
        {sub ? <span className="text-xs font-normal text-slate-400 ml-2">{sub}</span> : null}
      </div>
    </div>
  );
}
